import React, { FC, useEffect, useState } from 'react';
import classNames from 'classnames';

import DataBlock from 'components/ui/dataBlock';
import RoundButton from 'components/ui/roundButton';
import PaymentDataMapper from '../PaymentDataMapper';
import { CallbackInfo } from '../callbackInfo';
import { useTabsScroll } from 'hooks/useTabsScroll';
import { useTranslation } from 'hooks/useTranslation';
import { AnyObject } from 'types/Common';

interface Props {
  operation: AnyObject;
  transactionId: string;
}

interface TabDefinition {
  title: string;
  content: React.ReactNode;
}

const OperationInfoWl19: FC<Props> = ({ operation, transactionId }) => {
  const getTranslate = useTranslation();
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const {
    tabsRef,
    canScrollLeft,
    canScrollRight,
    scrollLeft,
    scrollRight,
    recalculate,
  } = useTabsScroll();

  if (!operation) return null;

  const {
    operationInfo,
    cryptoInfo,
    paymentMethodInfo,
    paymentProviderInfo,
    secure,
    address,
    callback,
    deviceInfo,
    fraud,
    vatBillingInfo,
  } = PaymentDataMapper.getOperationInfo(operation);

  const {
    operationId,
    callback: rawCallback,
    availableActions,
  } = operation;
  const hasCallback = rawCallback?.callbackBody;
  const resendCallbackEnabled = availableActions?.callbackResend?.isEnabled;

  const alwaysTabs: TabDefinition[] = [
    {
      title: getTranslate('payments.single.operationInfo.header'),
      content: (
        <DataBlock
          title='payments.single.operationInfo.header'
          data={operationInfo}
          theme='modern'
        />
      ),
    },
    {
      title: getTranslate('payments.single.providerInfo.header'),
      content: (
        <DataBlock
          title='payments.single.providerInfo.header'
          data={paymentProviderInfo}
          theme='modern'
        />
      ),
    },
    {
      title: getTranslate('payments.data.paymentMethod.header'),
      content: (
        <DataBlock
          title='payments.data.paymentMethod.header'
          data={paymentMethodInfo}
          theme='modern'
        />
      ),
    },
    {
      title: getTranslate('payments.single.deviceInfo.header'),
      content: (
        <DataBlock
          title='payments.single.deviceInfo.header'
          data={deviceInfo}
          theme='modern'
        />
      ),
    },
    {
      title: getTranslate('payments.single.callback.header'),
      content: (
        <DataBlock
          title='payments.single.callback.header'
          data={callback}
          theme='modern'
        />
      ),
    },
    {
      title: getTranslate('payments.single.3DSecure.header'),
      content: (
        <DataBlock
          title='payments.single.3DSecure.header'
          data={secure}
          theme='modern'
        />
      ),
    },
  ];

  const optionalTabs: TabDefinition[] = [
    ...(hasCallback
      ? [
        {
          title: getTranslate('payments.single.lastcallback.header'),
          content: (
            <CallbackInfo
              transactionId={transactionId}
              operationId={operationId}
              callback={rawCallback}
              resendEnabled={resendCallbackEnabled}
            />
          ),
        },
      ]
      : []),
    ...(address.isVisible
      ? [
        {
          title: getTranslate('payments.single.address.header'),
          content: (
            <DataBlock
              title='payments.single.address.header'
              data={address.content}
              theme='modern'
            />
          ),
        },
      ]
      : []),
    ...(fraud.isFraud
      ? [
        {
          title: getTranslate('payments.single.fraud.header'),
          content: (
            <DataBlock
              title='payments.single.fraud.header'
              data={fraud.content}
              theme='modern'
            />
          ),
        },
      ]
      : []),
    ...(cryptoInfo.isVisible
      ? [
        {
          title: getTranslate('payments.single.cryptoInfo.header'),
          content: (
            <DataBlock
              title='payments.single.cryptoInfo.header'
              data={cryptoInfo.content}
              theme='modern'
            />
          ),
        },
      ]
      : []),
    ...(vatBillingInfo.isVisible
      ? [
        {
          title: getTranslate('payments.single.BillingInfo.header'),
          content: (
            <DataBlock
              title='payments.single.BillingInfo.header'
              data={vatBillingInfo.content}
              theme='modern'
            />
          ),
        },
      ]
      : []),
  ];

  const tabs = [...alwaysTabs, ...optionalTabs];

  const safeActiveIndex = Math.min(activeTabIndex, tabs.length - 1);
  useEffect(() => {
    recalculate();
  }, [optionalTabs.length, recalculate]);

  return (
    <div className='payment-card__operation-info'>
      <div className='payment-card__operation-info-tabs-row'>
        <div
          ref={tabsRef}
          className='payment-card__operation-info-tabs'
          style={{ scrollbarWidth: 'none' }}>
          {tabs.map((tab, index) => (
            <button
              key={tab.title}
              type='button'
              onClick={() => setActiveTabIndex(index)}
              className={classNames('payment-card__operation-info-tab', {
                'payment-card__operation-info-tab--active':
                  index === safeActiveIndex,
                'payment-card__operation-info-tab--inactive':
                  index !== safeActiveIndex,
              })}>
              {tab.title}
            </button>
          ))}
        </div>
        <div className='eto-swiper-buttons-container'>
          <RoundButton
            icon='etoArrowLeft'
            size={32}
            iconSize={12}
            disabled={!canScrollLeft}
            onClick={scrollLeft}
            customClass='eto-swiper-button'
          />
          <RoundButton
            icon='etoArrowRight'
            size={32}
            iconSize={12}
            disabled={!canScrollRight}
            onClick={scrollRight}
            customClass='eto-swiper-button'
          />
        </div>
      </div>

      <div className='payment-card__operation-info-panel'>
        {tabs[safeActiveIndex]?.content}
      </div>
    </div>
  );
};

export default OperationInfoWl19;
